/**
 * Upload queue (spec §8.10 steps 4–5, §16.2 step 5): IndexedDB `uploads` records, OPFS blobs,
 * `POST /api/assets/init` → `PUT` with XHR progress; retried when the browser comes back online.
 */
import { api, unwrap } from "../api/client";
import { db } from "../sync/persistence";
import { getBlob, removeBlob } from "./opfsBlobCache";

export type UploadState = "queued" | "uploading" | "done" | "failed";
export interface UploadRecord {
  hash: string;
  name: string;
  mime: string;
  size: number;
  state: UploadState;
  error?: string;
  crdtDocId?: string;
  treeId?: string;
}

type Listener = (rec: UploadRecord & { progress?: number }) => void;
const listeners = new Map<string, Set<Listener>>();
const progress = new Map<string, number>();
let draining = false;

export function subscribeUpload(hash: string, cb: Listener): () => void {
  if (!listeners.has(hash)) listeners.set(hash, new Set());
  listeners.get(hash)!.add(cb);
  void (async () => {
    const rec = await (await db()).get("uploads", hash);
    if (rec) cb({ ...(rec as UploadRecord), progress: progress.get(hash) });
  })();
  return () => listeners.get(hash)?.delete(cb);
}

function emit(rec: UploadRecord, p?: number) {
  if (p !== undefined) progress.set(rec.hash, p);
  for (const cb of listeners.get(rec.hash) ?? []) cb({ ...rec, progress: progress.get(rec.hash) });
}

export async function enqueueUpload(rec: Omit<UploadRecord, "state">): Promise<void> {
  const full: UploadRecord = { ...rec, state: "queued" };
  await (await db()).put("uploads", full as never);
  emit(full, 0);
  void drain();
}

async function save(rec: UploadRecord) {
  await (await db()).put("uploads", rec as never);
}

export async function drain(): Promise<void> {
  if (draining || !navigator.onLine) return;
  draining = true;
  try {
    const all = (await (await db()).getAll("uploads")) as UploadRecord[];
    for (const rec of all.filter((r) => r.state === "queued" || r.state === "uploading" || r.state === "failed")) {
      await uploadOne(rec);
    }
  } finally {
    draining = false;
  }
}

async function uploadOne(rec: UploadRecord): Promise<void> {
  try {
    const init = unwrap<{ exists: boolean; uploadUrl: string }>(
      await api.POST("/api/assets/init", { body: { hash: rec.hash, size: rec.size, mime: rec.mime, name: rec.name, crdtDocId: rec.crdtDocId ?? null, treeId: rec.treeId ?? null } }),
    );
    if (init.exists) {
      rec.state = "done";
      await save(rec);
      emit(rec, 1);
      await removeBlob(rec.hash).catch(() => {});
      return;
    }
    const blob = await getBlob(rec.hash);
    if (!blob) throw new Error("local blob missing");
    rec.state = "uploading";
    await save(rec);
    emit(rec, 0);
    await putWithProgress(init.uploadUrl, blob, rec.mime, (p) => emit(rec, p));
    rec.state = "done";
    delete rec.error;
    await save(rec);
    emit(rec, 1);
  } catch (e) {
    rec.state = "failed";
    rec.error = e instanceof Error ? e.message : String(e);
    await save(rec);
    emit(rec);
  }
}

function csrf(): string {
  return document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/)?.[1] ?? "";
}

function putWithProgress(url: string, blob: Blob, mime: string, onProgress: (p: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", mime || "application/octet-stream");
    xhr.setRequestHeader("X-CSRFToken", csrf());
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
    xhr.onload = () => (xhr.status < 300 ? resolve() : reject(new Error(`upload failed: ${xhr.status} ${xhr.responseText.slice(0, 200)}`)));
    xhr.onerror = () => reject(new Error("network error during upload"));
    xhr.send(blob);
  });
}

if (typeof window !== "undefined") {
  window.addEventListener("online", () => void drain());
  setTimeout(() => void drain(), 1500);
}
